/* eslint react/no-unused-state: 0 */

import { autobind } from 'core-decorators';
import React, { PureComponent } from 'react';
import OrderStatusEnum from 'const/OrderStatusEnum';
import { processing } from 'decorators';
import * as mainApi from 'services/api/main';
import { notify, withErrorNotification } from 'storage/NotificationsContext';
import mainWithContext from 'with/withContext';

type OrdersContextInterface = {
  ordersList: WoaRequest[];
  accountId?: number;
  isFetching: boolean;
  isReportSending: boolean;
  numOfResults?: number;
  keyword?: string;
  error?: Error;
  offset: number;
  totalOrders: number;
  sortBy?: string | null;
  sortDirection?: string | null;
  showAll?: boolean;
  getOrdersList: (
    accountId: number,
    params?: {
      keyword?: string;
      showAll?: boolean;
      sortBy?: string | null;
      sortDirection?: string | null;
      isSortingUpdate: boolean;
    },
    newOffset?: number | undefined,
  ) => void;
  reloadOrders: (showAll?: boolean) => void;
  requestOrdersReport: (dateFrom?: string, dateTo?: string) => void;
  updateOrdersList: () => void;
  updateSorting: (sortBy: string | null, sortDirection: string | null) => void;
};

const defaultValue: OrdersContextInterface = {
  ordersList: [],
  isFetching: true,
  isReportSending: false,
  offset: 0,
  totalOrders: 0,
  getOrdersList: () => {},
  reloadOrders: () => {},
  requestOrdersReport: () => {},
  updateOrdersList: () => {},
  updateSorting: () => {},
};

const sortByFieldMap: Record<string, string> = {
  articlePublicationStatus: 'article.woadPublicationStatus',
  author: 'authorName',
  journalName: 'article.journalName',
};

const OrdersContext = React.createContext<OrdersContextInterface>(defaultValue);

function withProvider(WrappedComponent) {
  @autobind
  class OrdersProviderWrapper extends PureComponent<any, OrdersContextInterface> {
    state = { ...defaultValue, isFetching: true, getOrdersList: this.getOrdersList, numOfResults: 15, offset: 0 };

    @withErrorNotification
    @processing('isFetching')
    async getOrdersList(
      accountId: number,
      {
        isSortingUpdate,
        keyword,
        showAll,
        sortBy,
        sortDirection,
      }: {
        keyword?: string;
        showAll?: boolean;
        sortBy?: string | null;
        sortDirection?: string | null;
        isSortingUpdate?: boolean;
      } = {},
      newOffset = undefined,
    ) {
      let { numOfResults, offset } = this.state;
      const newNumOfResults = isSortingUpdate ? this.state.ordersList.length : numOfResults;
      const showAllChanged = this.state.showAll !== showAll;

      // Preserve existing sort settings if not explicitly provided
      const currentSortBy = sortBy ?? this.state.sortBy;
      const currentSortDirection = sortDirection ?? this.state.sortDirection;

      if (newOffset !== undefined) {
        offset = newOffset;
      }

      if (isSortingUpdate || this.state.keyword !== keyword || showAllChanged) {
        offset = 0;
      }

      const query = {
        statuses: [OrderStatusEnum.FUNDING_CONFIRMED].join(','),
      };

      this.setState({
        accountId,
        offset,
        keyword,
        sortBy: currentSortBy,
        sortDirection: currentSortDirection,
        showAll,
        requestOrdersReport: (dateFrom, dateTo) =>
          this.requestOrdersReport(accountId, query.statuses, dateFrom, dateTo, keyword),
      });

      try {
        if (showAll) {
          query.statuses = [OrderStatusEnum.FUNDING_CONFIRMED, OrderStatusEnum.DENIED, OrderStatusEnum.CANCELLED].join(
            ',',
          );
        }
        const [ordersList, totalOrders] = await Promise.all([
          mainApi.getOrders(
            accountId,
            keyword,
            query.statuses,
            newNumOfResults,
            offset,
            currentSortBy,
            currentSortDirection,
          ),
          mainApi.getRequestCount(accountId, query.statuses),
        ]);
        this.setState(state => ({
          error: null,
          ...state,
          ordersList: offset === 0 ? ordersList : state.ordersList.concat(ordersList),
          totalOrders: totalOrders > 0 ? totalOrders : 0,
        }));
      } catch (error) {
        this.setState({ error, ordersList: [] });

        throw error;
      }
    }

    @withErrorNotification
    @processing('isReportSending')
    async requestOrdersReport(accountId, statuses, dateFrom, dateTo, keyword) {
      await mainApi.requestOrdersReport(accountId, statuses, dateFrom, dateTo, keyword);
      notify.info('Report is being generated. It will be sent by email upon completion.');
    }

    reloadOrders() {
      const { accountId, getOrdersList, keyword, showAll } = this.state;

      if (accountId) {
        getOrdersList(accountId, { keyword, showAll });
      }
    }

    updateOrdersList() {
      if (this.state.totalOrders > this.state.offset + this.state.numOfResults) {
        this.setState(
          state => {
            const newOffset = state.ordersList.length;
            return { ...state, offset: newOffset };
          },
          () => {
            this.reloadOrders();
          },
        );
      }
    }

    updateSorting(sortBy: string | null, sortDirection: string | null) {
      this.setState({ sortBy, sortDirection }, () => {
        const { accountId, keyword, showAll } = this.state;
        if (accountId) {
          this.getOrdersList(accountId, { keyword, showAll, sortBy, sortDirection, isSortingUpdate: true });
        }
      });
    }

    render() {
      return (
        <OrdersContext.Provider
          value={{
            ...this.state,
            reloadOrders: this.reloadOrders,
            updateOrdersList: this.updateOrdersList,
            updateSorting: this.updateSorting,
          }}
        >
          <WrappedComponent {...this.props} />
        </OrdersContext.Provider>
      );
    }
  }

  return OrdersProviderWrapper;
}

export { OrdersContextInterface };
export default {
  useContext: () => React.useContext(OrdersContext),
  withProvider,
  withContext: mainWithContext(OrdersContext),
};
